import { Button, InputGroup, Switch } from '@blueprintjs/core';
import permissions from '@hogwarts/permissions';
import { REGION_DISPLAY_LABELS } from '@hogwarts/plugin-cognita/lib/jobs/report_utils';
import {
  Error,
  Loading,
  PageTitle,
  Select,
} from '@hogwarts/ui-components-core';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import { PageHeader, TimezoneWarning } from '../../components';
import InsightsList from '../../components/Insights/InsightsList';
import {
  AnalyticsContext,
  FederationContext,
  OrganisationContext,
  UserContext,
} from '../../context';
import {
  useBuildReport,
  useFeature,
  useMutation,
  usePermission,
  useQuery,
} from '../../hooks';
import { isFeatureEnabled } from '../../hooks/useFeature';
import {
  CREATE_REPORT,
  DELETE_REPORT,
  UPDATE_REPORT,
  UPDATE_USER_PREFERENCES,
} from '../../mutations';
import {
  GET_CURRENT_USER,
  GET_REPORTS,
  GET_REPORTSResponse,
} from '../../queries';
import { H1 } from '../../ui-dom-components';
import insightComponentRegistry from './components';
import styles from './styles.module.css';
import { getExportTypeProps } from './utils';

const Insights = () => {
  const organisation = useContext(OrganisationContext);
  const federation = useContext(FederationContext);
  const analytics = useContext(AnalyticsContext);
  const user = useContext(UserContext);

  const viewEnabled = useFeature('reports.view');
  const duplicateEnabled = useFeature('reports.add');

  let [selectedInsightId, setSelectedInsightId] = useQueryParam(
    'id',
    StringParam
  );
  const [isNewInsight] = useQueryParam('new', BooleanParam);
  const [searchQuery, setSearchQuery] = useQueryParam('q', StringParam);
  const [ownerTypeFilter, setOwnerTypeFilter] = useQueryParam(
    'owner',
    StringParam
  );
  const [sortBy, setSortBy] = useQueryParam('sort', StringParam);

  // Track if we've loaded from localStorage to prevent overwriting
  const hasLoadedFromStorageRef = React.useRef(false);

  // Load and restore preferences from localStorage
  useEffect(() => {
    // Skip if we've already loaded or if we're viewing an insight
    if (hasLoadedFromStorageRef.current || selectedInsightId) return;

    const savedPreferences = localStorage.getItem('insights-preferences');
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        // Only load if we don't already have values from URL params
        if (preferences.searchQuery && !searchQuery) {
          setSearchQuery(preferences.searchQuery);
        }
        if (preferences.ownerTypeFilter && !ownerTypeFilter) {
          setOwnerTypeFilter(preferences.ownerTypeFilter);
        }
        if (preferences.sortBy && !sortBy) {
          setSortBy(preferences.sortBy);
        }
        hasLoadedFromStorageRef.current = true;
      } catch (error) {
        console.warn('Failed to parse saved insights preferences:', error);
        hasLoadedFromStorageRef.current = true;
      }
    } else {
      hasLoadedFromStorageRef.current = true;
    }
  }, [selectedInsightId, searchQuery, ownerTypeFilter, sortBy]);

  // Restore preferences when returning from insight view to list view
  useEffect(() => {
    const isReturningToList =
      !selectedInsightId && hasLoadedFromStorageRef.current;
    const hasNoCurrentValues = !searchQuery && !ownerTypeFilter && !sortBy;

    if (isReturningToList && hasNoCurrentValues) {
      const savedPreferences = localStorage.getItem('insights-preferences');
      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          if (preferences.searchQuery) {
            setSearchQuery(preferences.searchQuery);
          }
          if (preferences.ownerTypeFilter) {
            setOwnerTypeFilter(preferences.ownerTypeFilter);
          }
          if (preferences.sortBy) {
            setSortBy(preferences.sortBy);
          }
        } catch (error) {
          console.warn(
            'Failed to parse saved insights preferences on return:',
            error
          );
        }
      }
    }
  }, [selectedInsightId, searchQuery, ownerTypeFilter, sortBy]);

  // Save preferences to localStorage only when we have explicit values
  useEffect(() => {
    if (!hasLoadedFromStorageRef.current) return; // Don't save until we've loaded

    // Only save if we have at least one explicit value (not undefined)
    // This prevents overwriting localStorage with empty values when navigating to root URL
    const hasExplicitValues =
      searchQuery !== undefined ||
      ownerTypeFilter !== undefined ||
      sortBy !== undefined;

    if (hasExplicitValues) {
      const preferences = {
        searchQuery: searchQuery || '',
        ownerTypeFilter: ownerTypeFilter || '',
        sortBy: sortBy || 'favourite',
      };
      localStorage.setItem('insights-preferences', JSON.stringify(preferences));
    }
  }, [searchQuery, ownerTypeFilter, sortBy]);

  const {
    data: reports,
    loading,
    error,
  } = useQuery<GET_REPORTSResponse[]>(GET_REPORTS, {
    selector: 'organisations[0].reports',
    variables: {
      organisationKey: organisation.key,
    },
  });
  const [createReport] = useMutation(CREATE_REPORT, {
    selector: 'createReport',
    refetchQueries: [
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });
  const [updateReport] = useMutation(UPDATE_REPORT, {
    selector: 'updateReport',
    refetchQueries: [
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });

  const stripTypenameDeep = <T,>(value: T): T => {
    // Removes Apollo cache `__typename` fields so payloads are valid inputs.
    return JSON.parse(
      JSON.stringify(value, (key, v) => (key === '__typename' ? undefined : v))
    ) as T;
  };
  const [deleteReport] = useMutation(DELETE_REPORT, {
    refetchQueries: [
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });
  const [updateUserPreferences] = useMutation(UPDATE_USER_PREFERENCES, {
    refetchQueries: [
      { query: GET_CURRENT_USER },
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });

  const canDeleteOrganisationReports = usePermission(
    permissions.REPORT_DELETE,
    organisation?.id
  );
  const canDeleteFederationReports = usePermission(
    permissions.REPORT_DELETE,
    federation?.id
  );

  const canReadIncidents = usePermission(
    permissions.PROFILE_ACTIVITY_READ,
    organisation?.id
  );

  const [
    exportReport,
    {
      component: BuildingReportComponent,
      props: reportProps,
      disabled: reportDisabled,
    },
  ] = useBuildReport();

  const reports2 = useMemo(() => {
    if (!reports) return null;

    // Grab User favourites
    const favourites = user.preferences?.favourites?.reports || {};

    let result = reports
      .filter((r) => {
        const options = r.options || {};
        if (options.hidden) return false;

        if (r.viewType === 'incidents' && !canReadIncidents) {
          return false;
        }

        if (
          options.hideIfNoSharedTeam === true &&
          (!federation?.hasSharedTeam || !federation?.canReadSharedProfiles)
        ) {
          return false;
        }

        if (
          options.featureKey &&
          !isFeatureEnabled(options.featureKey, organisation)
        ) {
          return false;
        }
        return true;
      })
      .map((report) => {
        let favourite = favourites[report.id];
        let canDelete;
        let sharedWith;
        switch (report.ownerType) {
          case 'FEDERATION': {
            canDelete = canDeleteFederationReports;
            // Check if this is a region-tagged report
            if (report.options?.organisationTags?.length > 0) {
              const tagKey = report.options.organisationTags[0];
              let displayLabel;
              if (tagKey) {
                // Try both original and uppercased keys for REGION_DISPLAY_LABELS
                displayLabel =
                  REGION_DISPLAY_LABELS[tagKey] ||
                  REGION_DISPLAY_LABELS[tagKey.toUpperCase()];
              }
              if (!displayLabel) {
                // Fallback to showing the tagKey itself if no label found
                displayLabel = tagKey || 'Unknown';
              }
              sharedWith = `Shared with Federation organizations with the ${displayLabel} region tag`;
            } else {
              sharedWith = 'Shared with Federation';
            }
            break;
          }
          case 'ORGANISATION': {
            canDelete = canDeleteOrganisationReports;
            sharedWith = 'Shared with Organisation';
            break;
          }
          case 'USER': {
            sharedWith = 'Only Visible to you';
            canDelete = true;
            break;
          }
          default:
          case 'SYSTEM': {
            if (favourite == null) {
              favourite = true;
            }
            canDelete = false;
            break;
          }
        }

        if (report.options?.preventDeletion === true) {
          canDelete = false;
        }

        if (report.locked) {
          canDelete = false;
        }

        const Component = report.viewType
          ? insightComponentRegistry[report.viewType]
          : null;

        const exportType = report.exportType?.[0];

        // For profiles reports, show export options based on viewType
        let exportButtonProps;
        let hasExportFunction = false;

        if (report.viewType === 'incomplete-profiles') {
          exportButtonProps = {
            multipleExports: true,
            exportOptions: [
              {
                type: 'incomplete-profiles-pdf',
                icon: 'file-pdf',
                text: 'Export PDF',
                intent: 'danger',
                allowed: !reportDisabled,
              },
              {
                type: 'incomplete-profiles-excel',
                icon: 'file-excel',
                text: 'Export Excel',
                intent: 'success',
                allowed: !reportDisabled,
              },
            ],
          };
          hasExportFunction = true;
        } else if (report.sourceType === 'profiles') {
          exportButtonProps = {
            multipleExports: true,
            exportOptions: [
              {
                type: 'profiles-pdf',
                icon: 'file-pdf',
                text: 'Export PDF',
                intent: 'danger',
                allowed: !reportDisabled,
              },
              {
                type: 'profiles-excel',
                icon: 'file-excel',
                text: 'Export Excel',
                intent: 'success',
                allowed: !reportDisabled,
              },
            ],
          };
          hasExportFunction = true;
        } else if (exportType) {
          exportButtonProps = getExportTypeProps(exportType, !reportDisabled);
          hasExportFunction = true;
        }

        return {
          ...report,
          canDelete,
          favourite: !!favourite,
          sharedWith,
          Component,
          exportButtonProps,
          canDuplicate: duplicateEnabled,
          onDuplicate: duplicateEnabled
            ? async () => {
                analytics.events.insights.duplicateClicked({
                  insightId: report.id,
                  insightName: report.name,
                });
                const { data: newReport } = await createReport({
                  variables: {
                    ...report,
                    name: `Copy of ${report.name}`,
                    ownerType: 'USER',
                    ownerId: user.id,
                    options: {
                      ...report.options,
                      hideIfNoSharedTeam: false,
                      preventEditing: false,
                      preventDeletion: false,
                    },
                  },
                });

                // Special case: Upcoming Expiration Dates report relies on `groups`
                // for the left-hand sidebar. Duplicates should keep these groups.
                if (
                  newReport?.id &&
                  report?.options?.featureKey === 'reports.upcoming_expiry_dates' &&
                  Array.isArray((report as any)?.groups) &&
                  (report as any).groups.length
                ) {
                  const groupsInput = stripTypenameDeep(
                    (report as any).groups.map(
                      ({ key, label, condition }: any) => ({
                        key,
                        label,
                        condition,
                      })
                    )
                  );
                  await updateReport({
                    variables: {
                      reportId: newReport.id,
                      groups: groupsInput,
                    },
                  });
                }

                return newReport;
              }
            : null,
          onDelete: () => {
            if (report.locked) {
              return Promise.resolve(false);
            }
            analytics.events.insights.deleteClicked({
              insightId: report.id,
              insightName: report.name,
            });
            return deleteReport({
              variables: {
                reportId: report.id,
              },
            });
          },
          onFavourite: (value: boolean) => {
            analytics.events.insights.favouriteClicked({
              insightId: report.id,
              insightName: report.name,
              favourited: value,
            });
            return updateUserPreferences({
              variables: {
                updatedPreferences: {
                  favourites: {
                    reports: {
                      ...favourites,
                      [report.id]: value,
                    },
                  },
                },
              },
            });
          },
          onExport: hasExportFunction
            ? (exportTypeParam?: string) => {
                // For profiles reports, use the passed export type; for others, use the report's exportType
                const finalExportType = exportTypeParam || exportType;
                if (finalExportType) {
                  analytics.events.insights.exportClicked({
                    insightId: report.id,
                    insightName: report.name,
                  });
                  return exportReport(report.id, finalExportType, {
                    organisationKey: organisation?.key,
                    organisationId: organisation?.id,
                    federationId: federation?.id,
                  });
                }
                return null;
              }
            : null,
          onView:
            (!exportType || viewEnabled) && Component
              ? () => {
                  analytics.events.insights.viewClicked({
                    insightId: report.id,
                    insightName: report.name,
                  });
                  setSelectedInsightId(report.id);
                }
              : null,
        };
      });

    return [
      ...result.filter((c) => c.favourite === true),
      ...result.filter((c) => c.favourite !== true),
    ];
  }, [
    reports,
    user,
    federation,
    organisation,
    reportDisabled,
    duplicateEnabled,
    viewEnabled,
    canDeleteFederationReports,
    canDeleteOrganisationReports,
    analytics,
    createReport,
    deleteReport,
    updateUserPreferences,
    exportReport,
    setSelectedInsightId,
  ]);

  const filteredReports = useMemo(() => {
    if (!reports2) return reports2;
    let list = reports2;

    // Apply owner type filter
    if (ownerTypeFilter && ownerTypeFilter !== 'all') {
      list = list.filter((r) => r.ownerType === ownerTypeFilter);
    }

    // Apply search filter
    if (searchQuery && searchQuery.trim().length > 0) {
      const s = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(s) ||
          (r.description || '').toLowerCase().includes(s) ||
          (r.sharedWith || '').toLowerCase().includes(s)
      );
    }

    // Apply sorting (default to favourites first if no sort specified)
    const currentSort = sortBy || 'favourite';
    if (currentSort) {
      list = [...list].sort((a, b) => {
        switch (currentSort) {
          case 'name':
            return (a.name || '').localeCompare(b.name || '');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '');
          case 'favourite':
            return (b.favourite ? 1 : 0) - (a.favourite ? 1 : 0);
          case 'owner':
            return (a.ownerType || '').localeCompare(b.ownerType || '');
          default:
            return 0;
        }
      });
    }

    return list;
  }, [reports2, ownerTypeFilter, searchQuery, sortBy]);

  const ownerTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'USER', label: 'Yours' },
    { value: 'ORGANISATION', label: 'Organisation' },
    { value: 'FEDERATION', label: 'Federation' },
    { value: 'SYSTEM', label: 'System' },
  ];

  const sortOptions = [
    { value: 'favourite', label: 'Favourites First' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'owner', label: 'By Owner Type' },
  ];

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error />;
  }

  if (selectedInsightId) {
    const insight = reports2!.find((report) => report.id === selectedInsightId);
    if (!insight && isNewInsight) {
      return <Loading />;
    } else if (!insight) {
      return <Error title="Unknown Insight" />;
    }

    if (!insight.Component) return <Error title="Unknown Component" />;
    const Component = insight.Component as any;

    return (
      <>
        <PageTitle title="Insights" />
        <PageHeader
          // @ts-ignore
          header={
            <div className={styles.headerContainer}>
              <p className={styles.headerOrganisationName}>
                {organisation.name}
              </p>
              <H1 className={styles.selectorCurrent}>{insight.name}</H1>
            </div>
          }
          notificationChildren={<TimezoneWarning />}
        />
        <BuildingReportComponent {...reportProps} />
        <Component
          insight={insight}
          onExport={(params: any, exportType?: string) => {
            // For profiles reports, use the passed export type; for others, use the report's exportType
            const finalExportType = exportType || insight.exportType?.[0];
            if (finalExportType) {
              return exportReport(insight.id, finalExportType, { ...params });
            }
            return exportReport(insight.id, undefined, { ...params });
          }}
        />
      </>
    );
  } else {
    return (
      <>
        <PageTitle title="Insights" />
        <PageHeader
          header={organisation && organisation.name}
          actions={[
            {
              text: '',
              onClick: undefined,
              component: (
                <div style={{ minWidth: '260px' }}>
                  <InputGroup
                    placeholder="Search insights..."
                    value={searchQuery || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(e.target.value || undefined)
                    }
                  />
                </div>
              ),
            },
            {
              text: '',
              onClick: undefined,
              component: (
                <div style={{ minWidth: '140px' }}>
                  <Select
                    value={ownerTypeFilter || 'all'}
                    onChange={(item: { value: string; label: string }) =>
                      setOwnerTypeFilter(
                        item.value === 'all' ? undefined : item.value
                      )
                    }
                    valueField="value"
                    textField="label"
                    items={ownerTypeOptions}
                  />
                </div>
              ),
            },
            {
              text: '',
              onClick: undefined,
              component: (
                <div style={{ minWidth: '140px' }}>
                  <Select
                    value={sortBy || 'favourite'}
                    onChange={(item: { value: string; label: string }) =>
                      setSortBy(item.value)
                    }
                    valueField="value"
                    textField="label"
                    items={sortOptions}
                  />
                </div>
              ),
            },
          ]}
          notificationChildren={<TimezoneWarning />}
        />
        <BuildingReportComponent {...reportProps} />

        <div className="container-lg mt-3 h-100">
          {filteredReports && filteredReports.length > 0 ? (
            <>
              <div style={{ height: 'calc(100vh - 250px)' }}>
                <InsightsList insights={filteredReports} />
              </div>
              <div
                className="text-muted text-center mt-3 mb-3"
                style={{ paddingBottom: '2rem' }}
              >
                Showing {filteredReports.length}
                {reports2 ? ` of ${reports2.length}` : ''} insights
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fas fa-search fa-3x"></i>
              </div>
              <h4 className="text-muted">No insights found</h4>
              <p className="text-muted">
                {searchQuery || ownerTypeFilter
                  ? 'Try adjusting your search or filters'
                  : 'No insights are available'}
              </p>
              {(searchQuery || ownerTypeFilter) && (
                <Button
                  minimal
                  intent="primary"
                  onClick={() => {
                    setSearchQuery(undefined);
                    setOwnerTypeFilter(undefined);
                    setSortBy(undefined);
                  }}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
};

export default Insights;
